import { FormikProvider, useFormik } from 'formik';
import { useCallback, useId } from 'react';
import * as yup from 'yup';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import {
  useAdminCreateUserRegisterMutation,
  useAdminRegisterListQuery,
} from '@/modules/admin-dashboard/queries/registers.tsx';
import { Button, ModalContentWrapper, SelectPaginate, SelectPaginateOption } from 'ui';
import { CreateUserRegisterRequest } from 'api';

const validationSchema = yup.object({
  register_id: yup
    .number()
    .required('Please select register')
    .moreThan(0, 'Please select register'),
});

const initialValues = {
  user_id: 0,
  register_id: 0,
};

export interface AdminAddIssuerRegisterModalProps {
  issuer_id: number;
  issuer_register_ids: number[];
  open: boolean;
  onClose: () => void;
}

export const AdminAddIssuerRegisterModal = ({
  open,
  onClose,
  issuer_register_ids,
  issuer_id,
}: AdminAddIssuerRegisterModalProps) => {
  const formik = useFormik<CreateUserRegisterRequest>({
    initialValues: {
      ...initialValues,
      user_id: issuer_id,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      await addUserRegister(values);
      resetForm();
      onClose();
    },
    enableReinitialize: true,
  });

  const { mutateAsync: addUserRegister } = useAdminCreateUserRegisterMutation({
    setErrors: formik.setErrors,
  });

  const { fetchNextPage: fetchNextRegisterListPage } = useAdminRegisterListQuery(
    {
      page: 1,
      page_size: 15,
    },
    { disabled: true, cacheKey: useId() },
  );

  const handleAsyncRegisterList = useCallback(
    async (page: number, search?: string) => {
      const response = await fetchNextRegisterListPage({ pageParam: { page, search } });

      let array: SelectPaginateOption<{ id: number }>[] = [];

      response?.data?.pages.forEach(p => {
        if (p.page === page) {
          array = p.data
            .filter(({ id }) => !issuer_register_ids.includes(id))
            .map(({ name, id }) => {
              return {
                label: name,
                value: { id, toString: () => name },
              };
            });
        }
      });

      return array;
    },
    [fetchNextRegisterListPage, issuer_register_ids],
  );

  const handleChangeRegister = (register_id: number) => {
    formik.setFieldValue('register_id', register_id);
  };

  const handleCloseModalWithResetForm = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <ModalContentWrapper
      open={open}
      title="Add new register"
      onClose={handleCloseModalWithResetForm}
    >
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col space-y-10" onSubmit={preventFormDefault}>
          <div className="w-full flex flex-col space-y-8">
            <SelectPaginate<{ id: number }>
              onAsync={handleAsyncRegisterList}
              label="REGISTER"
              onChange={({ value }) => handleChangeRegister(value.id)}
              error={formik.errors.register_id}
            />
          </div>
          <Button onClick={formik.submitForm} loading={formik.isSubmitting}>
            Confirm
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
